import {
  ApolloClient,
  ApolloLink,
  from,
  fromPromise,
  HttpLink,
  InMemoryCache,
  Observable,
  Operation,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import fetch from 'isomorphic-fetch';

import CookieUtils from '../cookies';
import { fieldPolicies } from './typePolicies/fieldPolicies';
import QueryTypePolicies from './typePolicies/query';
import Auth from 'services/auth';
import { API_BASE, API_GRAPHQL } from 'constants/api';
import { AUTHENTICATION_ACTIONS, ddGlobalSessionId, telemetry } from 'utils/telemetry';

import type { NormalizedCacheObject } from '@apollo/client';
import type { ErrorHandler } from '@apollo/client/link/error';

const isBrowserEnv = typeof window !== 'undefined';

// Polyfill fetch() on the server (used by apollo-client)
if (!isBrowserEnv) {
  (global as unknown as typeof globalThis & { fetch: typeof fetch }).fetch = fetch;
}

let apolloClient: null | ApolloClient<NormalizedCacheObject> = null;

const authMiddleware = new ApolloLink((operation, forward) => {
  if (localStorage && typeof window !== 'undefined') {
    operation.setContext({
      headers: {
        ...CookieUtils.getAuthorizationHeader(),
        'x-springhealth-client-path': window.location.pathname,
        'x-springhealth-magic-id': ddGlobalSessionId.getSessionId(),
        'x-springhealth-client-type': 'Spring Health Admin Portal',
      },
    });
  }

  return forward(operation);
});

const updateOperationContext = (operation: Operation) => {
  operation.setContext(({ headers }) => ({
    headers: {
      ...headers,
      ...CookieUtils.getAuthorizationHeader(),
    },
  }));
};

export const handleOnErrorLink: ErrorHandler = ({ networkError, operation, forward }) => {
  // Check that networkError has a statusCode property, since it can also be a vanilla JS Error object.
  const isUnauthorizedNetworkError = networkError && 'statusCode' in networkError && networkError.statusCode === 401;

  if (isUnauthorizedNetworkError) {
    return fromPromise(Auth.refreshAccessToken()).flatMap(() => {
      updateOperationContext(operation);

      return forward(operation);
    });
  }

  // Note: We dont return anything because we don't want to double call graphQL requests if they've errored
  // Above: We've attempted to refresh an invalid token, so we can forward(...) the request again and retry
  // See docs: https://www.apollographql.com/docs/react/api/link/apollo-link-error
  return;
};

const errorLink = onError(handleOnErrorLink);

const clientLinkFetchOptions = {
  credentials: 'same-origin',
};

const rotomClientLink = new HttpLink({
  uri: `${API_BASE}${API_GRAPHQL}`,
  fetchOptions: {
    ...clientLinkFetchOptions,
  },
});

const http = () => rotomClientLink;

function create() {
  return new ApolloClient({
    /**
     * NOTE - Do not set connectToDevTools to false.
     *
     * See https://springhealth.atlassian.net/wiki/x/uADdX
     * See https://springhealth.atlassian.net/browse/CORE-2126
     */
    connectToDevTools: true,
    ssrMode: !isBrowserEnv, // Disables forceFetch on the server (so queries are only run once)
    link: from([authMiddleware, errorLink, http()]),
    cache: new InMemoryCache({
      typePolicies: {
        ...QueryTypePolicies,
        ...fieldPolicies,
      },
    }),
  });
}

export default function initApollo() {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!isBrowserEnv) {
    return create();
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create();
  }

  return apolloClient;
}
